// src/pages/AboutPage.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AboutPage.css';

const AboutPage = () => {
  const navigate = useNavigate();

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShopNowClick = () => {
    navigate('/products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactClick = () => {
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-pattern"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">📍</span>
              Washington DC's Trusted Source
            </div>
            <h1 className="hero-title">
              About <span className="title-accent">Designers DC</span>
            </h1>
            <p className="hero-subtitle">
              Washington DC's Premier i71 Compliant Cannabis Delivery Service
            </p>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" 
              fill="var(--color-background)"/>
          </svg>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-content">
              <span className="section-tag">Our Mission</span>
              <h2 className="section-title">Premium Quality, <span className="text-primary">Responsibly Delivered</span></h2>
              <p className="mission-text">
                At Designers DC, we're committed to providing safe, legal access to premium 
                cannabis products while strictly adhering to Washington DC's Initiative 71 
                regulations. We believe in responsible consumption, education, and creating 
                positive experiences for our community.
              </p>
              <div className="mission-stats">
                <div className="stat-item">
                  <span className="stat-number">100+</span>
                  <span className="stat-label">Happy Customers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Premium Products</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
              </div>
            </div>
            <div className="mission-image">
              <div className="image-grid">
                <div className="image-item">🌿</div>
                <div className="image-item">⚖️</div>
                <div className="image-item">🔬</div>
                <div className="image-item">🚚</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* i71 Compliance Section */}
      <section className="compliance-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Initiative 71</span>
            <h2 className="section-title">Legal & <span className="text-primary">Compliant</span></h2>
            <p className="section-subtitle">Understanding Washington DC's cannabis laws</p>
          </div>

          <div className="compliance-grid">
            <div className="compliance-card legal">
              <div className="card-icon-wrapper">
                <div className="card-icon">⚖️</div>
              </div>
              <h3>What's Legal</h3>
              <ul className="compliance-list">
                <li>Possession of up to 2 ounces of cannabis for personal use</li>
                <li>Cultivation of up to 6 plants (3 mature, 3 immature)</li>
                <li>Transfer of up to 1 ounce to another adult 21+ at no cost</li>
                <li>Consumption on private property</li>
              </ul>
            </div>

            <div className="compliance-card restrictions">
              <div className="card-icon-wrapper">
                <div className="card-icon">🚫</div>
              </div>
              <h3>Important Restrictions</h3>
              <ul className="compliance-list">
                <li>No sales or purchases (gifting only under I-71)</li>
                <li>No public consumption</li>
                <li>No driving under the influence</li>
                <li>No transportation across state lines</li>
              </ul>
            </div>
          </div>

          <div className="compliance-note">
            <div className="note-icon">ℹ️</div>
            <div className="note-content">
              <h3>Our Compliance Commitment</h3>
              <p>
                Designers DC operates in full compliance with DC's Initiative 71. 
                We provide premium cannabis products as gifts with the purchase 
                of our delivery service, educational materials, or merchandise. 
                All transactions are conducted legally within DC's regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Simple Process</span>
            <h2 className="section-title">How It <span className="text-primary">Works</span></h2>
            <p className="section-subtitle">Legal, safe, and convenient delivery in 4 easy steps</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">🔍</div>
              <h3>Browse & Select</h3>
              <p>
                Explore our premium selection of cannabis products. 
                Each item comes with a free gift in accordance with I-71.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">🆔</div>
              <h3>Age Verification</h3>
              <p>
                Provide valid government-issued ID showing you're 21+. 
                We verify all customers before processing orders.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">🚚</div>
              <h3>Compliant Delivery</h3>
              <p>
                Our professional drivers deliver your order discreetly. 
                All transactions follow DC's I-71 gifting regulations.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">✨</div>
              <h3>Enjoy Responsibly</h3>
              <p>
                Consume safely on private property. Store securely away 
                from children and pets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Responsibility */}
      <section className="safety-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Safety First</span>
            <h2 className="section-title">Our <span className="text-primary">Commitment</span></h2>
            <p className="section-subtitle">To your safety and community wellbeing</p>
          </div>

          <div className="safety-grid">
            <div className="safety-card">
              <div className="safety-icon">🧪</div>
              <h3>Lab Tested</h3>
              <p>All products third-party tested for potency and purity. No contaminants.</p>
            </div>

            <div className="safety-card">
              <div className="safety-icon">👨‍👩‍👧‍👦</div>
              <h3>Community Education</h3>
              <p>Resources about responsible consumption and DC cannabis laws.</p>
            </div>

            <div className="safety-card">
              <div className="safety-icon">🚗</div>
              <h3>Safe Delivery</h3>
              <p>Professional drivers, discreet vehicles for your privacy.</p>
            </div>

            <div className="safety-card">
              <div className="safety-icon">🔞</div>
              <h3>Age Verification</h3>
              <p>Strict 21+ policy enforced. Valid photo ID required.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title">Frequently Asked <span className="text-primary">Questions</span></h2>
            <p className="section-subtitle">Common questions about I-71 and our service</p>
          </div>

          <div className="faq-grid">
            <div className="faq-card">
              <h3>Is this legal?</h3>
              <p>Yes. We operate in full compliance with Washington DC's Initiative 71.</p>
            </div>

            <div className="faq-card">
              <h3>Do I need to be 21?</h3>
              <p>Yes. Valid government-issued photo ID required at delivery.</p>
            </div>

            <div className="faq-card">
              <h3>Delivery areas?</h3>
              <p>We deliver throughout Washington DC. No federal properties.</p>
            </div>

            <div className="faq-card">
              <h3>Delivery time?</h3>
              <p>Most deliveries within 1-2 hours with real-time tracking.</p>
            </div>

            <div className="faq-card">
              <h3>Payment methods?</h3>
              <p>Cash upon delivery.</p>
            </div>

            <div className="faq-card">
              <h3>Discreet delivery?</h3>
              <p>Yes. Unmarked vehicles and professional packaging.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Age Verification Banner */}
      <section className="age-banner">
        <div className="container">
          <div className="banner-content">
            <div className="banner-icon">🔞</div>
            <div className="banner-text">
              <h3>Age 21+ Verification Required</h3>
              <p>
                By using our service, you confirm that you are 21 years of age or older. 
                Valid government-issued photo ID required at delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Experience Premium Cannabis?</h2>
            <p className="cta-text">Browse our selection of premium products delivered legally to your door</p>
            <div className="cta-actions">
              <button onClick={handleShopNowClick} className="btn btn-primary btn-large">
                🛍️ Shop Now
              </button>
              <button onClick={handleContactClick} className="btn btn-outline btn-large">
                📞 Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="disclaimer-section">
        <div className="container">
          <div className="disclaimer-content">
            <h3>Legal Disclaimer</h3>
            <p>
              Designers DC operates in compliance with Washington DC's Initiative 71. 
              We provide cannabis as gifts with the purchase of delivery services, 
              educational materials, or merchandise. Cannabis remains illegal under 
              federal law. Consumption may impair ability to drive or operate machinery. 
              Keep away from children and pets. Use responsibly.
            </p>
            <p className="disclaimer-note">
              By using our service, you acknowledge that you understand and agree 
              to comply with all applicable DC laws and regulations.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;